// ─────────────────────────────────────────────────────────────────────────────
// TrainingScreen.js
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, ScrollView, TouchableOpacity, StyleSheet, SafeAreaView, StatusBar, RefreshControl, Linking, Alert } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';

const API = 'https://api.jobeng.ai';
const KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const P = '#0e4e96';
const getH = async () => { const t = await AsyncStorage.getItem('auth_token').catch(() => null); return { 'Content-Type': 'application/json', 'x-api-key': KEY, ...(t ? { Authorization: `Bearer ${t}` } : {}) }; };

const SAMPLE_MODULES = [
  { id: 1, title: 'Health & Safety Induction', category: 'Compliance', duration: '2h', status: 'completed', progress: 100, mandatory: true },
  { id: 2, title: 'GDPR & Data Protection', category: 'Compliance', duration: '1.5h', status: 'in-progress', progress: 60, mandatory: true },
  { id: 3, title: 'Leadership Fundamentals', category: 'Leadership', duration: '4h', status: 'not-started', progress: 0, mandatory: false },
  { id: 4, title: 'Excel Advanced', category: 'Technical', duration: '3h', status: 'not-started', progress: 0, mandatory: false },
  { id: 5, title: 'Customer Service Excellence', category: 'Soft Skills', duration: '2h', status: 'completed', progress: 100, mandatory: false },
];

const STATUS_COLORS = { completed: '#10b981', 'in-progress': '#f59e0b', 'not-started': '#94a3b8' };

export function TrainingScreen({ navigation, route }) {
  const empId = route?.params?.employeeId || '1';
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    try {
      const h = await getH();
      const res = await fetch(`${API}/api/data/training?employee_id=${parseInt(empId)||0}`, { headers: h });
      const json = await res.json().catch(() => ({}));
      const data = Array.isArray(json?.data) ? json.data : SAMPLE_MODULES;
      setModules(data.length ? data : SAMPLE_MODULES);
    } catch { setModules(SAMPLE_MODULES); }
    finally { setLoading(false); setRefreshing(false); }
  }, [empId]);

  useEffect(() => { load(); }, [load]);

  const filtered = filter === 'all' ? modules : modules.filter(m => m.status === filter);
  const completed = modules.filter(m => m.status === 'completed').length;
  const mandatory = modules.filter(m => m.mandatory && m.status !== 'completed').length;

  return (
    <SafeAreaView style={st.safe}>
      <StatusBar barStyle="light-content" backgroundColor={P} />
      <LinearGradient colors={[P, '#1e7be2']} style={st.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={st.back}><MaterialCommunityIcons name="arrow-left" size={22} color="#fff" /></TouchableOpacity>
        <Text style={st.headerTitle}>Training</Text>
        <View style={{ width: 36 }} />
      </LinearGradient>
      <ScrollView refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); load(); }} colors={[P]} />}>
        {mandatory > 0 && (
          <View style={st.alertBanner}>
            <MaterialCommunityIcons name="alert-circle" size={18} color="#f59e0b" />
            <Text style={st.alertText}>{mandatory} mandatory training{mandatory > 1 ? 's' : ''} incomplete</Text>
          </View>
        )}
        <View style={st.statsRow}>
          {[
            { label: 'Completed', value: completed, color: '#10b981' },
            { label: 'In Progress', value: modules.filter(m => m.status === 'in-progress').length, color: '#f59e0b' },
            { label: 'Total', value: modules.length, color: P },
          ].map(({ label, value, color }) => (
            <View key={label} style={[st.statCard, { borderTopColor: color }]}>
              <Text style={[st.statVal, { color }]}>{value}</Text>
              <Text style={st.statLabel}>{label}</Text>
            </View>
          ))}
        </View>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={st.filters} contentContainerStyle={{ paddingHorizontal: 16, gap: 8 }}>
          {['all', 'completed', 'in-progress', 'not-started'].map(f => (
            <TouchableOpacity key={f} onPress={() => setFilter(f)} style={[st.chip, filter === f && st.chipActive]}>
              <Text style={[st.chipText, filter === f && st.chipTextActive]}>{f === 'all' ? 'All' : f.split('-').map(w => w[0].toUpperCase() + w.slice(1)).join(' ')}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
        <View style={st.list}>
          {filtered.map((m, i) => (
            <TouchableOpacity key={m.id || i} style={st.moduleCard} onPress={() => Alert.alert(m.title, `Duration: ${m.duration}\nCategory: ${m.category}\nProgress: ${m.progress}%`, [{ text: 'Close' }, { text: 'Start', onPress: () => {} }])}>
              <View style={[st.moduleIcon, { backgroundColor: STATUS_COLORS[m.status] + '20' }]}>
                <MaterialCommunityIcons name={m.status === 'completed' ? 'check-circle' : m.status === 'in-progress' ? 'play-circle' : 'book-open-variant'} size={24} color={STATUS_COLORS[m.status]} />
              </View>
              <View style={{ flex: 1, marginLeft: 12 }}>
                <View style={st.modTop}>
                  <Text style={st.moduleTitle} numberOfLines={1}>{m.title}</Text>
                  {m.mandatory && <View style={st.mandatoryBadge}><Text style={st.mandatoryText}>REQUIRED</Text></View>}
                </View>
                <Text style={st.moduleSub}>{m.category} • {m.duration}</Text>
                {m.status === 'in-progress' && (
                  <View style={st.progressRow}>
                    <View style={st.progressBar}><View style={[st.progressFill, { width: `${m.progress}%` }]} /></View>
                    <Text style={st.progressPct}>{m.progress}%</Text>
                  </View>
                )}
              </View>
              <MaterialCommunityIcons name="chevron-right" size={20} color="#94a3b8" />
            </TouchableOpacity>
          ))}
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const st = StyleSheet.create({
  safe: { flex: 1, backgroundColor: '#f8fafc' },
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 14 },
  back: { padding: 4 },
  headerTitle: { color: '#fff', fontSize: 18, fontWeight: '700' },
  alertBanner: { flexDirection: 'row', alignItems: 'center', gap: 8, margin: 16, padding: 12, backgroundColor: '#fffbeb', borderRadius: 12, borderWidth: 1, borderColor: '#fde68a' },
  alertText: { color: '#92400e', fontWeight: '600', fontSize: 13 },
  statsRow: { flexDirection: 'row', padding: 16, gap: 8 },
  statCard: { flex: 1, backgroundColor: '#fff', borderRadius: 12, padding: 12, alignItems: 'center', borderTopWidth: 3, shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 4, elevation: 1 },
  statVal: { fontSize: 24, fontWeight: '900' },
  statLabel: { fontSize: 10, color: '#64748b', fontWeight: '600' },
  filters: { marginBottom: 8 },
  chip: { paddingHorizontal: 14, paddingVertical: 7, borderRadius: 20, borderWidth: 1.5, borderColor: '#e2e8f0', backgroundColor: '#fff' },
  chipActive: { backgroundColor: P, borderColor: P },
  chipText: { fontSize: 12, fontWeight: '600', color: '#64748b' },
  chipTextActive: { color: '#fff' },
  list: { padding: 16, gap: 8 },
  moduleCard: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', borderRadius: 14, padding: 14, shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 4, elevation: 1 },
  moduleIcon: { width: 48, height: 48, borderRadius: 14, alignItems: 'center', justifyContent: 'center' },
  modTop: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  moduleTitle: { flex: 1, fontSize: 14, fontWeight: '700', color: '#1e293b' },
  mandatoryBadge: { backgroundColor: '#fee2e2', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 5 },
  mandatoryText: { fontSize: 8, fontWeight: '800', color: '#ef4444' },
  moduleSub: { fontSize: 12, color: '#64748b', marginTop: 2 },
  progressRow: { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 6 },
  progressBar: { flex: 1, height: 4, backgroundColor: '#e2e8f0', borderRadius: 2 },
  progressFill: { height: 4, borderRadius: 2, backgroundColor: '#f59e0b' },
  progressPct: { fontSize: 11, fontWeight: '700', color: '#f59e0b' },
});

export default TrainingScreen;
